/**
 * Today's words (slice 4b spec §5): the greeting, "since", each Recent row's
 * meta and right-hand label, the filter, the working card's time, the week's
 * days and what ran. No I/O.
 */
import { shortTime } from "../chats/format";
import type { RanItem, RecentItem, Today } from "./api";

/** Routines aren't Recent's: their runs are Ran's (spec §5). */
export const FILTERS = ["all", "chat", "doc"] as const;
export type Filter = (typeof FILTERS)[number];

export const FILTER_LABELS: Record<Filter, string> = {
  all: "All",
  chat: "Chats",
  doc: "Docs",
};

const hourIn = (now: Date, timeZone: string) =>
  Number(
    new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone }).format(now),
  );

/** "Good morning, Tharshan." before noon, afternoon before 6 pm, else evening. */
export function greeting(name: string, timeZone: string, now: Date): string {
  const hour = hourIn(now, timeZone);
  const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  return `Good ${part}, ${name.trim().split(/\s+/)[0]}.`;
}

const dayKey = (at: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone, dateStyle: "short" }).format(at);

const clock = (at: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" })
    .format(at)
    .replace(" AM", " am")
    .replace(" PM", " pm");

const weekday = (at: Date, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(at);

/**
 * "since Fri 6:40 pm" from their last look, "since 6:40 am" the same day;
 * null before their first look, when it's only the 24-hour default (spec §5).
 */
export function sinceLabel(today: Pick<Today, "since" | "seen">, timeZone: string, now: Date) {
  if (!today.seen) return null;
  const at = new Date(today.since);
  const day = dayKey(at, timeZone) === dayKey(now, timeZone) ? "" : `${weekday(at, timeZone)} `;
  return `since ${day}${clock(at, timeZone)}`;
}

/** The date for the wide header: "Mon 26 Oct". */
export function shortDate(now: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
  })
    .format(now)
    .replace(",", "");
}

export function filterRecent(items: RecentItem[], filter: Filter): RecentItem[] {
  return filter === "all" ? items : items.filter((i) => i.kind === filter);
}

/** What a filtered list says with nothing in it. */
export function emptyLine(filter: Filter): string {
  if (filter === "all") return "Nothing yet. Ask for something below.";
  return `No ${FILTER_LABELS[filter].toLowerCase()} this week.`;
}

/** The small line under a row: its kind, then what places it (spec §5). */
export function metaOf(item: RecentItem): string {
  const parts: (string | null | undefined)[] = [];
  if (item.kind === "chat") {
    parts.push(
      "Chat",
      item.thread.messaging_app === "imessage"
        ? "via iMessage"
        : item.thread.messaging_app === "email"
          ? "by email"
          : null,
      item.thread.area?.label,
    );
  } else {
    parts.push(
      "Doc",
      item.area?.label,
      item.routine
        ? `updated by ${item.routine.title}`
        : item.via === "made"
          ? "made in your chat"
          : "updated",
    );
  }
  return parts.filter(Boolean).join(" · ");
}

/** The row's right-hand label: "Waiting on you", "Working", else when. */
export function rightOf(
  item: RecentItem,
  timeZone: string,
  now: Date,
): { text: string; tone: "waiting" | "working" | null } {
  if (item.kind === "chat" && item.status === "waiting") {
    return { text: "Waiting on you", tone: "waiting" };
  }
  if (item.kind === "chat" && item.status === "working")
    return { text: "Working", tone: "working" };
  return { text: shortTime(item.at, timeZone, now), tone: null };
}

/** The working card's time: "Working · 4 min", "Working · 1 h 5 min"; "Working" under a minute. */
export function workingFor(startedAt: string, now: Date): string {
  const min = Math.floor((now.getTime() - new Date(startedAt).getTime()) / 60_000);
  if (min < 1) return "Working";
  if (min < 60) return `Working · ${min} min`;
  const h = Math.floor(min / 60);
  return `Working · ${h} h${min % 60 ? ` ${min % 60} min` : ""}`;
}

/** A week row's day: "Today", "Tomorrow", else "Wed"; and its time, "7:00 am". */
export function weekDay(at: string, timeZone: string, now: Date): { day: string; time: string } {
  const when = new Date(at);
  const key = dayKey(when, timeZone);
  const today = dayKey(now, timeZone);
  // The next calendar date, not 24 hours on: a clock change makes a day 23 or 25 hours.
  const tomorrow = new Date(Date.parse(`${today}T00:00:00Z`) + 86_400_000)
    .toISOString()
    .slice(0, 10);
  const day = key === today ? "Today" : key === tomorrow ? "Tomorrow" : weekday(when, timeZone);
  return { day, time: clock(when, timeZone) };
}

/** The owner's line per person (slice 4 §9.1, 7): "Pallavi has 2 questions waiting since Fri." */
export function othersLine(
  o: NonNullable<Today["others_waiting"]>[number],
  timeZone: string,
  now: Date,
): string {
  const first = o.user.name.trim().split(/\s+/)[0];
  const what = o.count === 1 ? "one question" : `${o.count} questions`;
  const since = shortTime(o.since, timeZone, now);
  const when = since === "Yesterday" ? "yesterday" : since;
  return `${first} has ${what} from the crew waiting since ${when}.`;
}

/** A run's line in Ran: "Texted to you" when its reply was texted, else its summary's lead. */
export const ranLine = (item: RanItem): string | null =>
  item.texted ? "Texted to you" : item.line;

/** The phone's one line for Ran (spec §5): "3 routines ran this week"; null when none did. */
export function ranSummary(count: number): string | null {
  if (count === 0) return null;
  return `${count === 1 ? "1 routine" : `${count} routines`} ran this week`;
}
